import { useDispatch, useSelector } from "react-redux"
import { ApplicationState } from "../../interface/ApplicationState"
import FormLabel from "@mui/material/FormLabel"
import Grid from "@mui/material/Grid"
import OutlinedInput from "@mui/material/OutlinedInput"
import { DropdownSelector } from "../../components/DropdownSelector"
import InputAdornment from "@mui/material/InputAdornment"
import { DEFAULT_FUNDING_CURRENCY, getAllTypes } from "../../const"
import Skeleton from "@mui/material/Skeleton"
import { TradingPair } from "../../interface/TradingPair"
import { updateLocalUser } from "../../redux/actions/userActions"
import DeleteIcon from "@mui/icons-material/Delete"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import RefreshIcon from "@mui/icons-material/RestartAlt"
import FormControlLabel from "@mui/material/FormControlLabel"
import Switch from "@mui/material/Switch"
import Divider from "@mui/material/Divider"
import Card from "@mui/material/Card"
import Typography from "@mui/material/Typography"
import TextField from "@mui/material/TextField"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"
import { alpha, useTheme } from "@mui/material/styles"
import { Symbol } from "../../interface/Symbol"


interface MultiPairSectionProps
{
  symbols: Symbol[]
  tradingPair: TradingPair
  loading: boolean
}

export const MultiPairSection = ({ symbols, tradingPair, loading }: MultiPairSectionProps) =>
{
  const dispatch = useDispatch();
  const user = useSelector((state: ApplicationState) => state.userStore.user)
  const theme = useTheme()

  const handleRemove = (id: string) =>
  {
    if(!user.tradingPairs) return
    dispatch(updateLocalUser({ tradingPairs: user.tradingPairs.filter(p => p.pairId !== id)}))
  }

  const handleUpdatePair = (updatedPair: Partial<TradingPair>) =>
  {
    if(!user.tradingPairs) return
    const updatedPairs = user.tradingPairs.map((pair) => {
      if(pair.pairId === tradingPair.pairId) return { ...tradingPair, ...updatedPair, base: updatedPair?.pair?.slice(-4) || pair.base }
      return pair
    })

    dispatch(updateLocalUser({ tradingPairs: updatedPairs }))
  }

  return(
  <Card sx={styles.card(theme)}>
    <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} justifyContent="space-between" sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="overline" sx={styles.eyebrow(theme)}>Trading Pair</Typography>
        <Typography variant="h6" fontWeight="700">{tradingPair.pair}</Typography>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Chip label={`Funding ${tradingPair.base || "USDT"}`} sx={styles.chip(theme)} />
        <Chip label={`Interval ${tradingPair.interval || "1m"}`} sx={styles.chip(theme)} />
      </Stack>
    </Stack>
    <Divider sx={styles.divider(theme)} />

  <Grid container spacing={3} alignItems="center" sx={{ mb: 2 }}>

  <Grid item xl={2} lg={6} md={12} xs={12}>
      <FormLabel component="legend" style={styles.formLabel}>Current investment</FormLabel>
      <OutlinedInput 
        disabled
        sx={styles.input(theme)}
        startAdornment={<InputAdornment position="start">$</InputAdornment>} 
        endAdornment={<IconButton onClick={() => handleUpdatePair({ currentInvest: 0 })}><RefreshIcon /></IconButton>} 
        value={tradingPair.currentInvest} inputMode="numeric" fullWidth />
    </Grid>

  <Grid item xl={2} lg={6} md={12} xs={12}>
      <FormLabel component="legend" style={styles.formLabel}>Max invest for this pair</FormLabel>
      <OutlinedInput sx={styles.input(theme)} startAdornment={<InputAdornment position="start">$</InputAdornment>} value={tradingPair.maxInvest} inputMode="numeric" fullWidth placeholder="Max Invest" onChange={(e: any) => handleUpdatePair({ maxInvest: e.target.value }) } />
  </Grid>

    <Grid item xl={2} lg={6} md={12} xs={12}>
      { loading ? <Skeleton height={100} /> : <DropdownSelector currentItem={tradingPair.pair} onChangeItem={(pair) => handleUpdatePair({ pair })} title="The trading pair" items={symbols.map(item => item.symbol)} /> }
    </Grid>

    <Grid item xl={3} lg={3} md={12} xs={12}>

      <DropdownSelector 
        currentItem={tradingPair.base} 
        onChangeItem={(value) => handleUpdatePair({ base: value })}
        title="The funding currency the bot will use to buy"
        items={[DEFAULT_FUNDING_CURRENCY, "USDT", "BUSD" ]}/>
      </Grid>

      <Grid item xl={2} lg={2} md={12} xs={12}>

        <DropdownSelector 
          currentItem={tradingPair.interval} 
          onChangeItem={(value) => handleUpdatePair({ interval: value })}
          title="Candle Interval"
          items={getAllTypes()}/>
      </Grid>

    <Grid item xl={1} lg={1} md={12} xs={12} sx={{ mt: { xs: 0, xl: 4 } }}>
      <Button variant="text" color="error" sx={styles.deleteButton(theme)} startIcon={<DeleteIcon />} onClick={() => handleRemove(tradingPair.pairId)}>Delete</Button>
    </Grid>

    </Grid>

    <Grid item container direction="column"> 

      <Grid item xs={12} sx={styles.configBlock(theme)}>

        <Typography variant="subtitle1" fontWeight="700">Stop loss configuration</Typography>
        <Typography variant="caption" sx={styles.caption(theme)}>Dynamic stop loss is calculated by taking the markets daily stats low and high to produce a suitable loss percentage</Typography>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel label="Enable stop" control={<Switch checked={tradingPair.isStopLossEnabled} onChange={(_: any, checked: boolean) => handleUpdatePair({ isStopLossEnabled: checked })} />} />
        </Box>

        { tradingPair.isStopLossEnabled && <FormControlLabel sx={{ mt: 0.5, mb: 1 }} label={`Use dynamic stop ${tradingPair.stopLossPercentage}%`} control={<Switch checked={tradingPair.useDynamicStop} onChange={(_: any, checked: boolean) => handleUpdatePair({ useDynamicStop: checked })} />} /> }
        { tradingPair.isStopLossEnabled && !tradingPair.useDynamicStop && <Grid item><TextField sx={styles.textField(theme)} label="Stop Loss" type="number" value={tradingPair.stopLossPercentage} placeholder="Stop Percentage" onChange={(e: any) => handleUpdatePair({ stopLossPercentage: e.target.value })} InputProps={{ endAdornment: "%"}}/> </Grid>}
      </Grid>

      <Grid item xs={12} sx={styles.configBlock(theme)}>

        <Typography variant="subtitle1" fontWeight="700">Sell Profit Configuration</Typography>
        <Typography variant="caption" sx={styles.caption(theme)}>Dynamic stop loss is calculated by taking the markets daily stats low and high to produce a suitable loss percentage</Typography>

        <Box sx={{ mt: 1 }}>
          <FormControlLabel label="Enable long (don't ever sell at a loss)" control={<Switch checked={tradingPair.enableLong} onChange={(_: any, checked: boolean) => handleUpdatePair({ enableLong: checked })} />} />
        </Box>
        { !tradingPair.enableLong && <FormControlLabel sx={{ mt: 0.5, mb: 1 }} label="Sell only if profit threshold reached (min sell) and indicators bearish" control={<Switch checked={tradingPair.isMinSellProfitEnabled} onChange={(_: any, checked: boolean) => handleUpdatePair({ isMinSellProfitEnabled: checked })} />} /> }
         { tradingPair.isMinSellProfitEnabled && !tradingPair.enableLong && <Grid item><TextField sx={styles.textField(theme)} label="Threshold" type="number" value={tradingPair.takeProfitThreshold} placeholder="Sell Threshold" onChange={(e: any) => handleUpdatePair({ takeProfitThreshold: e.target.value })} InputProps={{ endAdornment: "%"}}/> </Grid>}


      </Grid>
    

    </Grid> 
  
    </Card>
  )
}

const styles = {
  formLabel: {
    marginBottom: 10
  },
  card: (theme: any) => ({
    marginBottom: 12,
    padding: 16,
    borderRadius: "16px",
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(180deg, ${alpha("#121f31", 0.92)} 0%, ${alpha("#0f1928", 0.96)} 100%)`
        : `linear-gradient(180deg, ${alpha("#ffffff", 0.88)} 0%, ${alpha("#edf5ff", 0.92)} 100%)`,
    boxShadow: "none",
  }),
  eyebrow: (theme: any) => ({
    display: "block",
    letterSpacing: 1.4,
    color: alpha(theme.palette.text.primary, 0.58),
  }),
  chip: (theme: any) => ({
    borderRadius: "10px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.68),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45)}`,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  }),
  divider: (theme: any) => ({
    mb: 2,
    borderColor: alpha(theme.palette.text.primary, 0.1),
  }),
  input: (theme: any) => ({
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.02 : 0.72),
    borderRadius: "12px",
  }),
  configBlock: (theme: any) => ({
    p: 1.5,
    mb: 1.25,
    borderRadius: "14px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.56),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.38)}`,
  }),
  caption: (theme: any) => ({
    color: alpha(theme.palette.text.primary, 0.64),
  }),
  textField: (theme: any) => ({
    mt: 0.5,
    '& .MuiOutlinedInput-root': {
      backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.02 : 0.72),
      borderRadius: "12px",
    },
  }),
  deleteButton: (theme: any) => ({
    borderRadius: "10px",
    color: theme.palette.error.main,
  }),
}
